import { Loader } from '@googlemaps/js-api-loader';
import { VenueAddress } from '../api-client';
import { getCountryCodeFromTLD, getCountryCodeFromTimezone } from '../helpers';

const loader = new Loader({
  apiKey: process.env.GOOGLE_MAPS_API_KEY,
  version: 'weekly',
});

type MapServices = {
  autocomplete: google.maps.places.AutocompleteService;
  geocoder: google.maps.Geocoder;
};

let mapServicesPromise: Promise<MapServices> | null = null;

async function initGoogleMapsAPIInternal(): Promise<MapServices> {
  const { AutocompleteService } = await loader.importLibrary('places');
  const { Geocoder } = await loader.importLibrary('geocoding');
  return {
    autocomplete: new AutocompleteService(),
    geocoder: new Geocoder(),
  };
}

export function initGoogleMapsAPI(): Promise<MapServices> {
  if (mapServicesPromise !== null) {
    return mapServicesPromise;
  }

  mapServicesPromise = initGoogleMapsAPIInternal();
  return mapServicesPromise;
}

const searchCountries = [getCountryCodeFromTimezone(), getCountryCodeFromTLD()];
export async function getPlacePredictions(
  input: string,
): Promise<google.maps.places.AutocompletePrediction[]> {
  const mapServices = await initGoogleMapsAPI();
  const predictions = await mapServices.autocomplete.getPlacePredictions({
    input,
    componentRestrictions: { country: searchCountries },
  });
  return predictions.predictions;
}

function findAddressComponent(
  addressComponents: google.maps.GeocoderAddressComponent[],
  match: RegExp,
  key: keyof google.maps.GeocoderAddressComponent = 'long_name',
) {
  return addressComponents
    .filter(e => e.types.find(type => type.match(match)))
    .map(e => e[key])
    .join(' ');
}

function getStreet(
  addressComponents: google.maps.GeocoderAddressComponent[],
  formattedAddress: string,
) {
  const streetNumber = findAddressComponent(
    addressComponents,
    /^street_number$/,
  );
  const route = findAddressComponent(addressComponents, /^route$/);
  const street = `${streetNumber} ${route}`.trim();
  if (!street.length) {
    // Google Maps fails to give us a valid street on rare occasions
    return formattedAddress.split(',')[0];
  }

  return street;
}

function parseGooglePlaceAddress(
  result: google.maps.GeocoderResult,
): VenueAddress {
  const {
    address_components: addressComponents = [],
    formatted_address: formattedAddress,
  } = result;
  return {
    street: getStreet(addressComponents, formattedAddress),
    city: findAddressComponent(addressComponents, /^(locality|postal_town)$/),
    postalCode: findAddressComponent(addressComponents, /^postal_code$/),
    countryCode: findAddressComponent(
      addressComponents,
      /^country$/,
      'short_name',
    ),
  };
}

export async function getPlaceAddress(placeId: string): Promise<VenueAddress> {
  const mapServices = await initGoogleMapsAPI();
  const placeResult = await mapServices.geocoder.geocode({ placeId });

  const parsedAddress = parseGooglePlaceAddress(placeResult.results[0]);

  // We can't always trust the place's address components, since they are entered manually
  // A city name can be abbreviated... The name of a neighborhood can be used as a city name... etc
  // This needs to be corrected because the server will reject the address as it will only be a partial match
  // So we use the postcode provided by the place to get accurate address components for the city
  const postcodeResult = await mapServices.geocoder.geocode({
    address: `${parsedAddress.postalCode} ${parsedAddress.city}`,
    componentRestrictions: { country: parsedAddress.countryCode }, // Unlike get place predictions, this can only take one country
  });
  const parsedPostCode = parseGooglePlaceAddress(postcodeResult.results[0]);

  if (parsedPostCode.city) {
    parsedAddress.city = parsedPostCode.city;
  }

  return parsedAddress;
}
